/**
 * IdCampsSection — upcoming ID camps for a school's sport.
 *
 * Positioned below the Coaches section, above the Ad slot.
 * Always rendered (empty state communicates that camp data belongs here).
 *
 * States:
 *   - loading (camps === undefined): skeleton row
 *   - empty (camps.length === 0): empty-state card
 *   - populated: list of CampCard components with optional Show all/fewer expander
 *
 * Design: WI-48 design artifact, Screens 1–9.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import type { CampDetail } from '../../api/school';
import { SkeletonRow } from '../ui/SkeletonRow';
import { CampCard } from './CampCard';

const VISIBLE_THRESHOLD = 4;

interface IdCampsSectionProps {
  /** undefined = still loading; [] = empty; non-empty array = data */
  camps: CampDetail[] | undefined;
  division: string;
  schoolName: string;
  websiteUrl: string | null;
  /** Sport slug for the browse-all link (e.g. "soccer_mens"). Defaults to "soccer_mens". */
  sport?: string;
}

export function IdCampsSection({
  camps,
  division,
  schoolName,
  websiteUrl,
  sport = 'soccer_mens',
}: IdCampsSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const isLoading = camps === undefined;
  const isD1 = division === 'ncaa_d1';

  const visibleCamps =
    !isLoading && camps !== undefined
      ? expanded || camps.length <= VISIBLE_THRESHOLD
        ? camps
        : camps.slice(0, VISIBLE_THRESHOLD)
      : [];

  const showExpander =
    !isLoading && camps !== undefined && camps.length > VISIBLE_THRESHOLD;

  return (
    <section
      aria-labelledby="camps-heading"
      aria-busy={isLoading ? 'true' : undefined}
    >
      <h2
        id="camps-heading"
        className="text-base font-semibold text-gray-900 mb-3"
      >
        ID Camps
      </h2>

      {isLoading ? (
        <SkeletonRow height="h-[88px]" className="rounded-lg" />
      ) : (
        <>
          {/* NCAA D1 compliance callout */}
          {isD1 && (
            <div
              role="note"
              className="flex gap-2.5 items-start
                         bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4
                         text-xs text-gray-700 leading-relaxed"
            >
              <InformationCircleIcon
                aria-hidden="true"
                className="w-4 h-4 flex-shrink-0 text-blue-500 mt-0.5"
              />
              <span>
                ID camps are typically subject to NCAA contact and evaluation
                rules. Confirm current eligibility at{' '}
                <a
                  href="https://www.ncaa.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-primary underline focus:outline-none
                             focus-visible:ring-2 focus-visible:ring-brand-primary rounded"
                >
                  ncaa.org
                </a>{' '}
                before registering.
              </span>
            </div>
          )}

          {/* Camp list or empty state */}
          {camps !== undefined && camps.length > 0 ? (
            <>
              <div className="flex flex-col gap-3">
                {visibleCamps.map((camp) => (
                  <CampCard
                    key={camp.camp_id}
                    camp={camp}
                    schoolName={schoolName}
                  />
                ))}
              </div>

              {showExpander && (
                <button
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => setExpanded((v) => !v)}
                  className="w-full text-center text-sm text-brand-primary font-medium
                             min-h-[44px] py-2 mt-1
                             focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary rounded"
                >
                  {expanded
                    ? 'Show fewer camps'
                    : `+ Show all ${camps.length} camps`}
                </button>
              )}
            </>
          ) : (
            <EmptyState schoolName={schoolName} websiteUrl={websiteUrl} sport={sport} />
          )}
        </>
      )}
    </section>
  );
}

interface EmptyStateProps {
  schoolName: string;
  websiteUrl: string | null;
  sport: string;
}

const SPORT_LABEL: Record<string, string> = {
  soccer_mens: "men's soccer",
  soccer_womens: "women's soccer",
};

function EmptyState({ schoolName, websiteUrl, sport }: EmptyStateProps) {
  const sportLabel = SPORT_LABEL[sport] ?? sport;

  return (
    <div className="rounded-lg border border-gray-200 shadow-sm p-4 bg-white text-sm text-gray-600">
      No upcoming camps on record.
      {websiteUrl && (
        <>
          {' '}Check the{' '}
          <a
            href={websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Check the ${schoolName} athletics site for the latest camp schedule`}
            className="text-brand-primary hover:underline
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary rounded"
          >
            {schoolName} athletics site
          </a>
          {' '}for the latest camp schedule.
        </>
      )}
      <div className="mt-2">
        <Link
          to={`/camps?sport=${encodeURIComponent(sport)}`}
          className="text-brand-primary hover:underline
                     focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary rounded"
        >
          Browse all upcoming {sportLabel} camps →
        </Link>
      </div>
    </div>
  );
}
